/**
 * i18next, wired for this app.
 *
 * One instance, initialised once as a side effect when this module is first
 * imported (from `main.tsx`). Catalogs are one JSON file per
 * (language, namespace) and are fetched lazily with a dynamic `import()`, so a
 * screen only pays for the namespaces it uses and only the enabled languages
 * are ever downloaded.
 *
 * Plurals resolve per the CLDR rules of each language — Romanian has three
 * forms, Russian four, Hebrew one/two/other — through i18next's own
 * `Intl.PluralRules` support: a key gets `_one` / `_few` / `_many` / `_other`
 * variants and `t('key', { count })` picks the right one. That is why
 * `plural()` in `lib/format` is being retired. (i18next-icu was tried and
 * dropped: it memoised a message under the language it was first compiled in
 * and returned it for every language, and it forced apostrophe/brace escaping
 * across every string. Native interpolation is `{{var}}`.)
 *
 * The active language owns three things: `<html lang>`, `<html dir>` and the
 * locale `lib/format` formats numbers and dates in. They are set here on every
 * `languageChanged`, so nothing else has to remember to.
 */
import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import resourcesToBackend from 'i18next-resources-to-backend';
import { setFormatLocale } from '@/lib/format';
import { dirFor } from './dir';
import { FALLBACK_LANG, SUPPORTED_LANGS } from './langs';

void i18n
  .use(resourcesToBackend((lang: string, ns: string) => import(`./locales/${lang}/${ns}.json`)))
  .use(initReactI18next)
  .init({
    lng: FALLBACK_LANG,
    fallbackLng: FALLBACK_LANG,
    supportedLngs: [...SUPPORTED_LANGS],
    // Load only what a screen asks for; `common` is the shared base and the
    // fallback namespace, so `t('save')` resolves from `common` on any screen
    // without each one having to load it.
    ns: ['common'],
    defaultNS: 'common',
    fallbackNS: 'common',
    interpolation: {
      // React already escapes; double-escaping mangles apostrophes and quotes.
      escapeValue: false,
    },
    returnNull: false,
    // An empty translation is a not-yet-translated key, not a blank label: fall
    // back to Romanian (the source of truth) rather than render nothing. This is
    // what lets a namespace be extracted first and translated afterwards.
    returnEmptyString: false,
    // No Suspense: a missing catalog must not blank the screen mid-render; the
    // fallback language (Romanian, the source of truth) fills any gap instead.
    react: { useSuspense: false },
  });

function applyDocumentLocale(lang: string): void {
  const root = document.documentElement;
  root.lang = lang;
  root.dir = dirFor(lang);
  setFormatLocale(lang);
}

applyDocumentLocale(FALLBACK_LANG);
i18n.on('languageChanged', applyDocumentLocale);

export default i18n;
